import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import adminApi from './api'
import { Notification } from './components/Notification'
import { ConfirmModal } from './components/ConfirmModal'


interface Event {
  id: string
  slug: string
  name: string
  description: string
  locationName?: string
  latitude?: number
  longitude?: number
  eventDate: string
  collectFrom?: string
  collectTo?: string
  targetTrees?: number
  targetAmount?: number
  status: 'planned' | 'active' | 'finished'
  isActiveDefault: boolean
  coverImage?: string
  createdAt: string
  updatedAt: string
}

export const EventsAdmin: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [notification, setNotification] = useState<{message: string, type: 'success' | 'error'} | null>(null)
  const [userRole, setUserRole] = useState<string>('')
  const [confirmDelete, setConfirmDelete] = useState<{show: boolean, id: string | null}>({show: false, id: null})
  const navigate = useNavigate()

  // Load user role from localStorage
  useEffect(() => {
    const userStr = localStorage.getItem('admin_user')
    if (userStr) {
      try {
        const user = JSON.parse(userStr)
        setUserRole(user.role || '')
      } catch (e) {
        // Failed to parse user data
      }
    }
  }, [])

  useEffect(() => {
    fetchEvents()
  }, [])

  const fetchEvents = async () => {
    try {
      const { data } = await adminApi.get('/api/admin/events')
      setEvents(data.events)
      setError('')
    } catch (err: unknown) {
      const errorMessage = err instanceof Error && 'response' in err
        ? ((err as any).response?.data?.message || 'Ошибка загрузки событий')
        : 'Ошибка загрузки событий'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = (id: string) => {
    setConfirmDelete({show: true, id})
  }

  const handleConfirmDelete = async () => {
    if (!confirmDelete.id) return

    try {
      await adminApi.delete(`/api/admin/events/${confirmDelete.id}`)
      setNotification({ message: 'Событие успешно удалено', type: 'success' })
      fetchEvents()
    } catch (err: unknown) {
      const errorMessage = err instanceof Error && 'response' in err
        ? ((err as any).response?.data?.message || 'Ошибка удаления события')
        : 'Ошибка удаления события'
      setNotification({ message: errorMessage, type: 'error' })
    } finally {
      setConfirmDelete({show: false, id: null})
    }
  }

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      planned: 'Запланировано',
      active: 'Активно',
      finished: 'Завершено'
    }
    return labels[status] || status
  }

  return (
    <div>
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="page-header flex items-center justify-between gap-4">
        <div>
          <h1 className="page-title">События</h1>
          <p className="page-subtitle">Управление экологическими событиями и активностями</p>
        </div>
        {userRole === 'admin' && (
          <button
            onClick={() => navigate('/admin/events/new')}
            className="btn-primary"
          >
            Создать событие
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-error mb-5">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="loading-spinner w-10 h-10"></div>
        </div>
      ) : (
        <div className="table-card">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="w-[35%]">Название</th>
                <th className="w-[120px]">Дата события</th>
                <th className="w-[20%]">Локация</th>
                <th className="w-[130px]">Статус</th>
                <th className="w-[130px]">По умолчанию</th>
                <th className="w-[50px]"></th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="empty-state">
                      <div className="empty-state-text">События не найдены</div>
                    </div>
                  </td>
                </tr>
              ) : (
                events.map(event => (
                  <tr
                    key={event.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/admin/events/${event.id}/edit`)}
                  >
                    <td>
                      <div className="font-medium text-[#1890ff]">{event.name}</div>
                      <div className="text-[13px] text-gray-500">{event.slug}</div>
                    </td>
                    <td>{new Date(event.eventDate.replace('Z', '')).toLocaleDateString('ru-RU')}</td>
                    <td>{event.locationName || '—'}</td>
                    <td>
                      <span className={`status-badge status-${event.status}`}>
                        {getStatusLabel(event.status)}
                      </span>
                    </td>
                    <td>
                      {event.isActiveDefault ? (
                        <span className="status-badge status-active inline-flex items-center gap-1">
                          <span className="text-sm">⭐</span>
                          По умолчанию
                        </span>
                      ) : (
                        <span className="text-gray-500">—</span>
                      )}
                    </td>
                    <td>
                      {userRole === 'admin' && (
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setOpenMenuId(openMenuId === event.id ? null : event.id)
                            }}
                            className="bg-transparent border-0 text-xl cursor-pointer px-2 py-1 text-gray-500 leading-none"
                          >
                            ⋮
                          </button>
                          {openMenuId === event.id && (
                            <>
                              <div
                                className="fixed inset-0 z-[999] cursor-default"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setOpenMenuId(null)
                                }}
                              />
                              <div className="absolute top-full right-0 mt-1 bg-white border border-[#d9d9d9] rounded-lg shadow-lg z-[1000] min-w-[160px]">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setOpenMenuId(null)
                                    navigate(`/admin/events/${event.id}/edit`)
                                  }}
                                  className="w-full px-4 py-2.5 text-left bg-transparent border-0 cursor-pointer text-sm text-black border-b border-[#f0f0f0] hover:bg-[#f5f5f5]"
                                >
                                  Редактировать
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setOpenMenuId(null)
                                    handleDelete(event.id)
                                  }}
                                  className="w-full px-4 py-2.5 text-left bg-transparent border-0 cursor-pointer text-sm text-[#ff4d4f] hover:bg-[#fff2f0]"
                                >
                                  Удалить
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmDelete.show}
        title="Подтвердите удаление"
        message="Вы уверены, что хотите удалить это событие? Это действие нельзя отменить."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete({show: false, id: null})}
        confirmText="Удалить"
        cancelText="Отмена"
        isDanger={true}
      />
    </div>
  )
}
